import { AnimatePresence, motion, springs } from "../../lib/motion/index.js";
import { useInsertPromptStore, useTasksStore } from "../../stores/index.js";
import { effectiveColorOf } from "../task-colors.js";
import { useMatrixPriorityColor } from "../priority-matrix/useEffectiveMatrixDisplay.js";
import { useI18n } from "../../lib/useI18n.js";
import { useCalendarRenderIndex } from "./CalendarRenderIndex.js";

function readableOn(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return "#fff";
  const n = parseInt(m[1]!, 16);
  return 0.299 * ((n >> 16) & 0xff) + 0.587 * ((n >> 8) & 0xff) + 0.114 * (n & 0xff) > 150 ? "#1f2937" : "#fff";
}

/** Small "Insert" tab that pops at the TOP EDGE of a subtask block when it was
 *  just dropped onto its own parent — press it to insert the subtask back inside
 *  the parent (clears its time). Colored like the task. Auto-dismisses after 2s. */
export function InsertButton({ taskId }: { taskId: string }) {
  const active = useInsertPromptStore((s) => s.active);
  const show = active?.taskId === taskId;
  return (
    <AnimatePresence>
      {show && <InsertButtonInner taskId={taskId} active={active} />}
    </AnimatePresence>
  );
}

function InsertButtonInner({
  taskId,
  active,
}: {
  taskId: string;
  active: { taskId: string; parentId: string };
}) {
  const { t } = useI18n();
  const dismiss = useInsertPromptStore((s) => s.dismiss);
  const nestUnder = useTasksStore((s) => s.nestUnder);
  const index = useCalendarRenderIndex();
  const tasks = useTasksStore.getState().tasks;
  const task = tasks.find((t) => t.id === taskId);
  const contextColor = index.contextColorOf(task ?? { calendar_id: null, topic_ids: [] });
  // Matrix scope: the pill tracks the block's continuous priority color.
  const matrixColor = useMatrixPriorityColor(task);
  const bg = (task && effectiveColorOf(task, tasks, 0, contextColor, matrixColor)) || "#3b82f6";

  return (
        <motion.button
          type="button"
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.85 }}
          transition={springs.snappy}
          draggable={false}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); void nestUnder(active!.taskId, active!.parentId); dismiss(); }}
          style={{ backgroundColor: bg, color: readableOn(bg) }}
          className="absolute bottom-full left-1/2 z-[60] -translate-x-1/2 whitespace-nowrap rounded-t-md rounded-b-none px-2 py-0.5 text-[calc(10px*var(--app-text-scale))] font-semibold shadow-md ring-1 ring-black/15"
        >
          {t("calendar.insert")}
        </motion.button>
  );
}
